<script setup lang="ts">
import { LEGAL_PRIVACY_URL, LEGAL_TERMS_URL } from '@/constants/legal'

/**
 * Casilla de aceptación de los Términos de uso y la Política de privacidad
 * (ADR-SEC-013 local). Desmarcada por defecto; los dos enlaces abren en pestaña
 * nueva y no alternan la casilla. La usan el registro y el modal de
 * consentimiento. El copy no menciona «IA» (ADR-SEC-010).
 */

interface Props {
  modelValue: boolean
  id?: string
  /** `true` ⇒ «…de privacidad actualizados» (modal «Actualizamos nuestros términos»). */
  updated?: boolean
  /** Resalta la casilla con «Acepta los términos para continuar». */
  highlighted?: boolean
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  id: 'legal-consent',
  updated: false,
  highlighted: false,
  disabled: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

function onChange(event: Event) {
  emit('update:modelValue', (event.target as HTMLInputElement).checked)
}
</script>

<template>
  <div
    data-testid="legal-consent"
    class="rounded-lg"
    :class="props.highlighted ? 'ring-2 ring-danger/60 bg-danger-bg p-3' : ''"
  >
    <div class="flex items-start gap-3">
      <input
        :id="props.id"
        type="checkbox"
        :checked="props.modelValue"
        :disabled="props.disabled"
        :aria-describedby="props.highlighted ? `${props.id}-hint` : undefined"
        class="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 accent-primary-500 cursor-pointer"
        @change="onChange"
      >
      <label
        :for="props.id"
        class="text-sm text-slate-600 leading-snug cursor-pointer"
      >
        Acepto los
        <a
          :href="LEGAL_TERMS_URL"
          target="_blank"
          rel="noopener noreferrer"
          class="font-semibold text-primary-500 hover:underline"
        >Términos de uso</a>
        y la
        <a
          :href="LEGAL_PRIVACY_URL"
          target="_blank"
          rel="noopener noreferrer"
          class="font-semibold text-primary-500 hover:underline"
        >Política de privacidad</a>
        {{ props.updated ? 'actualizados' : 'de Quizoteca' }}
      </label>
    </div>
    <p
      v-if="props.highlighted"
      :id="`${props.id}-hint`"
      role="alert"
      class="mt-2 text-xs font-medium text-danger"
    >
      Acepta los términos para continuar
    </p>
  </div>
</template>
